import cn from 'classnames';
import React, { memo } from 'react';
import { useTranslation } from 'next-i18next';

import { Icon } from 'components';
import { Row } from 'components/layout';
import { USERINFO_LABELS } from 'hooks/useCandidateProfile';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import CVSection from '../CVSection';
import ChromeExtensionWidget from '../ChromeExtensionWidget';
import ProfileProgress from './ProfileProgress';

import styles from './Sidebar.module.scss';

interface SidebarProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  onLabelClick: (label: USERINFO_LABELS) => void;
}

export const Sidebar: React.FC<SidebarProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    onLabelClick,
  }: SidebarProps) => {
    const {
      basicInfoComplete,
      educationComplete,
      experienceComplete,
      extraCurricularComplete,
      skillsComplete,
      demographiclInfoComplete,
      percentCompleted,
      completedTopics,
    } = useCandidateProfile();
    const [t] = useTranslation('candidate.profile');
    const contentClassNames = cn(
      {
        [styles.Sidebar]: true,
      },
      className
    );

    return (
      <div className={contentClassNames} style={style} data-testid="Sidebar">
        <div className={styles.main}>
          <ProfileProgress
            percentCompleted={percentCompleted || 0}
            completedTopics={completedTopics}
          />
          <section className={styles.recommendedSection}>
            <p className={styles.sectionTitle}>
              {t('sidebar.main.recommended')}
            </p>
            <button
              onClick={() => onLabelClick(USERINFO_LABELS.BASIC_INFO)}
              className={styles.sectionButton}
            >
              <Row
                align="center"
                justify="space-between"
                className={styles.sectionRow}
              >
                <Row
                  align="center"
                  className={cn(styles.sectionLabel, {
                    [styles.sectionLabelComplete]: basicInfoComplete,
                  })}
                >
                  <Icon
                    iconName="icon_profile"
                    className={cn(styles.iconIncomplete, {
                      [styles.iconComplete]: basicInfoComplete,
                    })}
                  />
                  <p>{t('sidebar.main.basic-info')}</p>
                </Row>
                {basicInfoComplete ? (
                  <Icon
                    key={1}
                    iconName="tick-mark-circle"
                    className={styles.tickCircleComplete}
                  />
                ) : (
                  <Icon
                    key={2}
                    iconName="icon_check"
                    className={styles.tickCircleIncomplete}
                  />
                )}
              </Row>
            </button>
            <button
              onClick={() => onLabelClick(USERINFO_LABELS.EDUCATION)}
              className={styles.sectionButton}
            >
              <Row
                align="center"
                justify="space-between"
                className={styles.sectionRow}
              >
                <Row
                  align="center"
                  className={cn(styles.sectionLabel, {
                    [styles.sectionLabelComplete]: educationComplete,
                  })}
                >
                  <Icon
                    iconName="icon_hat-university"
                    className={cn(styles.iconIncomplete, {
                      [styles.iconComplete]: educationComplete,
                    })}
                  />
                  <p>{t('sidebar.main.education')}</p>
                </Row>
                {educationComplete ? (
                  <Icon
                    key={1}
                    iconName="tick-mark-circle"
                    className={styles.tickCircleComplete}
                  />
                ) : (
                  <Icon
                    key={2}
                    iconName="icon_check"
                    className={styles.tickCircleIncomplete}
                  />
                )}
              </Row>
            </button>
            <button
              onClick={() => onLabelClick(USERINFO_LABELS.EXPERIENCE)}
              className={styles.sectionButton}
            >
              <Row
                align="center"
                justify="space-between"
                className={styles.sectionRow}
              >
                <Row
                  align="center"
                  className={cn(styles.sectionLabel, {
                    [styles.sectionLabelComplete]: experienceComplete,
                  })}
                >
                  <Icon
                    iconName="icon_briefcase"
                    className={cn(styles.iconIncomplete, {
                      [styles.iconComplete]: experienceComplete,
                    })}
                  />
                  <p>{t('sidebar.main.experience')}</p>
                </Row>
                {experienceComplete ? (
                  <Icon
                    key={1}
                    iconName="tick-mark-circle"
                    className={styles.tickCircleComplete}
                  />
                ) : (
                  <Icon
                    key={2}
                    iconName="icon_check"
                    className={styles.tickCircleIncomplete}
                  />
                )}
              </Row>
            </button>
          </section>
          <section>
            <p className={cn(styles.sectionTitle)}>
              {t('sidebar.main.optional')}
            </p>
            <button
              onClick={() =>
                onLabelClick(USERINFO_LABELS.EXTRA_CURRICULAR_ACTIVITIES)
              }
              className={styles.sectionButton}
            >
              <Row
                align="center"
                justify="space-between"
                className={styles.sectionRow}
              >
                <Row
                  align="center"
                  className={cn(styles.sectionLabel, {
                    [styles.sectionLabelComplete]: extraCurricularComplete,
                  })}
                >
                  <Icon
                    iconName="icon_crown"
                    className={cn(styles.iconIncomplete, {
                      [styles.iconComplete]: extraCurricularComplete,
                    })}
                  />
                  <p>{t('sidebar.main.extra-curricular')}</p>
                </Row>
                {extraCurricularComplete ? (
                  <Icon
                    key={1}
                    iconName="tick-mark-circle"
                    className={styles.tickCircleComplete}
                  />
                ) : (
                  <Icon
                    key={2}
                    iconName="icon_check"
                    className={styles.tickCircleIncomplete}
                  />
                )}
              </Row>
            </button>
            <button
              onClick={() => onLabelClick(USERINFO_LABELS.SKILLS)}
              className={styles.sectionButton}
            >
              <Row
                align="center"
                justify="space-between"
                className={styles.sectionRow}
              >
                <Row
                  align="center"
                  className={cn(styles.sectionLabel, {
                    [styles.sectionLabelComplete]: skillsComplete,
                  })}
                >
                  <Icon
                    iconName="icon_bicep"
                    className={cn(styles.iconIncomplete, {
                      [styles.iconComplete]: skillsComplete,
                    })}
                  />
                  <p>{t('sidebar.main.skills')}</p>
                </Row>
                {skillsComplete ? (
                  <Icon
                    key={1}
                    iconName="tick-mark-circle"
                    className={styles.tickCircleComplete}
                  />
                ) : (
                  <Icon
                    key={2}
                    iconName="icon_check"
                    className={styles.tickCircleIncomplete}
                  />
                )}
              </Row>
            </button>
            <button
              onClick={() => onLabelClick(USERINFO_LABELS.DEMOGRAPHIC_INFO)}
              className={styles.sectionButton}
            >
              <Row
                align="center"
                justify="space-between"
                className={styles.sectionRow}
              >
                <Row
                  align="center"
                  className={cn(styles.sectionLabel, {
                    [styles.sectionLabelComplete]: demographiclInfoComplete,
                  })}
                >
                  <Icon
                    iconName="icon_info"
                    className={cn(styles.iconIncomplete, {
                      [styles.iconComplete]: demographiclInfoComplete,
                    })}
                  />
                  <p>{t('sidebar.main.demographic')}</p>
                </Row>
                {demographiclInfoComplete ? (
                  <Icon
                    key={1}
                    iconName="tick-mark-circle"
                    className={styles.tickCircleComplete}
                  />
                ) : (
                  <Icon
                    key={2}
                    iconName="icon_check"
                    className={styles.tickCircleIncomplete}
                  />
                )}
              </Row>
            </button>
          </section>
          <ChromeExtensionWidget />
        </div>
        <CVSection />
      </div>
    );
  }
);

Sidebar.displayName = 'Sidebar';
